/**
 * Pure domain logic for NetBox-style Tenancy Cascade & Equipment Assignment.
 * Supports hierarchical inheritance, explicit overrides, multi-rack propagation, and tenant filtering.
 */

export interface EquipmentWithTenant {
  id: string;
  name: string;
  tenant?: string | null;
  attributes?: Record<string, any> | null;
  [key: string]: any;
}

export interface TenancyResolution {
  effectiveTenant: string;
  isInherited: boolean;
  isOverridden: boolean;
}

/**
 * Resolves the effective tenant of an equipment item.
 * NetBox rule:
 * 1. If equipment has an explicit tenant defined, that tenant wins (override).
 * 2. If equipment has no tenant, it inherits the rack's tenant.
 * 3. Default fallback is 'mine' (operator enterprise tenant).
 */
export function resolveEquipmentTenant(
  eqTenant?: string | null,
  rackTenant?: string | null
): TenancyResolution {
  const normRack = (rackTenant || 'mine').trim();
  const rawEq = eqTenant ? eqTenant.trim() : '';

  if (rawEq) {
    const isOverridden = normRack !== '' && rawEq !== normRack;
    return {
      effectiveTenant: rawEq,
      isInherited: false,
      isOverridden,
    };
  }

  return {
    effectiveTenant: normRack || 'mine',
    isInherited: normRack !== '' && normRack !== 'mine',
    isOverridden: false,
  };
}

/**
 * Cascades a tenant from a rack down to an array of installed equipment.
 * Sets both top-level `tenant` and `attributes.tenant` for persistent compatibility.
 */
export function cascadeTenancyToEquipment<T extends EquipmentWithTenant>(
  equipmentList: T[],
  newTenant: string
): { updatedList: T[]; changedCount: number } {
  const normTenant = (newTenant || 'mine').trim();
  let changedCount = 0;

  const updatedList = equipmentList.map(eq => {
    const currentTenant = (eq.tenant || '').trim();
    if (currentTenant === normTenant) {
      return eq;
    }

    changedCount++;
    const nextAttrs = {
      ...(eq.attributes || {}),
      tenant: normTenant,
    };

    return {
      ...eq,
      tenant: normTenant,
      attributes: nextAttrs,
    };
  });

  return { updatedList, changedCount };
}

/**
 * Filters a list of equipment by tenant.
 * Matches against resolved effective tenant.
 */
export function filterEquipmentByTenant<T extends EquipmentWithTenant>(
  equipmentList: T[],
  tenantFilter: string | null | undefined,
  rackTenant?: string | null
): T[] {
  if (!tenantFilter || tenantFilter === 'all') {
    return equipmentList;
  }

  const normFilter = tenantFilter.trim().toLowerCase();

  return equipmentList.filter(eq => {
    const { effectiveTenant } = resolveEquipmentTenant(eq.tenant, rackTenant);
    return effectiveTenant.toLowerCase() === normFilter;
  });
}

/**
 * Returns a sorted list of all unique effective tenant keys present across equipment and the rack.
 */
export function getUniqueTenantsFromEquipment<T extends EquipmentWithTenant>(
  equipmentList: T[],
  rackTenant?: string | null
): string[] {
  const set = new Set<string>();
  const baseRack = (rackTenant || 'mine').trim();
  if (baseRack) set.add(baseRack);

  for (const eq of equipmentList) {
    const { effectiveTenant } = resolveEquipmentTenant(eq.tenant, rackTenant);
    if (effectiveTenant) set.add(effectiveTenant);
  }

  return Array.from(set).sort();
}

/**
 * Batch cascades a tenant to multiple racks and optionally all their equipment in memory.
 */
export function batchCascadeTenancy<R extends { id: string; tenant?: string; isDirty?: boolean }, E extends EquipmentWithTenant>(
  racks: Record<string, R>,
  equipmentByRack: Record<string, E[]>,
  targetRackIds: string[],
  newTenant: string,
  cascadeToEquipment: boolean = true
): {
  updatedRacks: Record<string, R>;
  updatedEquipment: Record<string, E[]>;
  affectedRacksCount: number;
  affectedEquipmentCount: number;
} {
  const normTenant = (newTenant || 'mine').trim();
  const nextRacks = { ...racks };
  const nextEquipment = { ...equipmentByRack };

  let affectedRacksCount = 0;
  let affectedEquipmentCount = 0;

  for (const rackId of targetRackIds) {
    const r = nextRacks[rackId];
    if (r) {
      if ((r.tenant || 'mine').trim() !== normTenant) {
        nextRacks[rackId] = {
          ...r,
          tenant: normTenant,
          isDirty: true,
        };
        affectedRacksCount++;
      }
    }

    if (cascadeToEquipment && nextEquipment[rackId]) {
      const { updatedList, changedCount } = cascadeTenancyToEquipment(
        nextEquipment[rackId],
        normTenant
      );
      if (changedCount > 0) {
        nextEquipment[rackId] = updatedList;
        affectedEquipmentCount += changedCount;
      }
    }
  }

  return {
    updatedRacks: nextRacks,
    updatedEquipment: nextEquipment,
    affectedRacksCount,
    affectedEquipmentCount,
  };
}
